import { Card, Empty, Typography } from "antd";
import { useEffect, useRef } from "react";

import type { BuildingItem } from "./BuildingSearchResults";
import { BuildingSearchResults } from "./BuildingSearchResults";
import { UpdateRegistrationPanel } from "./UpdateRegistrationPanel";

const { Text } = Typography;

interface EazyModelUpdateContentProps {
  hasSearched: boolean;
  searchResults: BuildingItem[];
  registeredTarget: BuildingItem | null;
  switchTarget: BuildingItem | null;
  onRegisteredTargetChange: (target: BuildingItem | null) => void;
  onSwitchTargetChange: (target: BuildingItem | null) => void;
  onRefresh?: () => void;
  onLoadMore?: () => Promise<void>;
  loadingMore?: boolean;
  submitResult?: "success" | "error" | null;
  errorMessage?: string;
  onSubmitResultChange?: (result: "success" | "error" | null) => void;
  onErrorMessageChange?: (message: string) => void;
}

export const EazyModelUpdateContent = ({
  hasSearched,
  searchResults,
  registeredTarget,
  switchTarget,
  onRegisteredTargetChange,
  onSwitchTargetChange,
  onRefresh,
  onLoadMore,
  loadingMore = false,
  submitResult,
  errorMessage,
  onSubmitResultChange,
  onErrorMessageChange,
}: EazyModelUpdateContentProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevSearchResultsLengthRef = useRef<number>(searchResults.length);
  const hadDataBeforeEmptyRef = useRef<boolean>(false);

  useEffect(() => {
    const currentLength = searchResults.length;
    const prevLength = prevSearchResultsLengthRef.current;
    const isEmpty = currentLength === 0;
    const hadDataBefore = hadDataBeforeEmptyRef.current;

    if (prevLength > 0 && isEmpty) {
      hadDataBeforeEmptyRef.current = true;
    }

    if (hadDataBefore && !isEmpty && currentLength > 0) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
      hadDataBeforeEmptyRef.current = false;
    }

    prevSearchResultsLengthRef.current = currentLength;
  }, [searchResults.length]);

  return (
    <div className="flex h-full min-h-0 w-full gap-6">
      <Card
        className="flex h-full min-h-0 flex-1 flex-col"
        styles={{
          header: {
            borderBottom: "none",
          },
          body: {
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: 0,
          },
        }}
        title={<Text className="text-[18px] font-medium leading-[28px] text-[#101828]">対象選択</Text>}
      >
        {hasSearched && (
          <div className="mx-4 flex h-12 items-center justify-center rounded-md bg-gray-50 px-4">
            <Text className="text-xs font-normal leading-4 text-gray-500">
              左側の棟情報カードを右側の「登録済の対象」または「切替の対象」エリアにドラッグして移動してください。
            </Text>
          </div>
        )}
        <div className="custom-scrollbar mt-4 flex min-h-0 flex-1 flex-col overflow-y-auto" ref={scrollContainerRef}>
          {searchResults.length > 0 ? (
            <BuildingSearchResults
              containerRef={scrollContainerRef}
              loadingMore={loadingMore}
              onDragStart={() => {
                // drag start handler
              }}
              onLoadMore={onLoadMore}
              results={searchResults}
            />
          ) : hasSearched ? (
            <div className="flex h-full flex-col items-center justify-center">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center">
              <Text className="text-[18px] font-normal leading-[20px] text-[#00000040]">検索結果はここに表示</Text>
            </div>
          )}
        </div>
      </Card>

      <Card
        className="flex h-full min-h-0 flex-1 flex-col"
        styles={{
          header: {
            borderBottom: "none",
          },
          body: {
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            paddingTop: "0px",
          },
        }}
        title={<Text className="text-[18px] font-medium leading-[28px] text-[#101828]">登録情報を更新</Text>}
      >
        <UpdateRegistrationPanel
          errorMessage={errorMessage}
          onErrorMessageChange={onErrorMessageChange}
          onRefresh={onRefresh}
          onRegisteredTargetChange={onRegisteredTargetChange}
          onSubmitResultChange={onSubmitResultChange}
          onSwitchTargetChange={onSwitchTargetChange}
          registeredTarget={registeredTarget}
          submitResult={submitResult}
          switchTarget={switchTarget}
        />
      </Card>
    </div>
  );
};
